import { Divider, Stack } from 'component-library';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import {
  ConservationExplainerWell,
  NCCardSectionHeader,
  printLatestDate,
  useNCCardContext,
} from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';

export const WaterCardStatsTile = () => {
  const isLargeScreen = useScreenSize() === 'large';

  return (
    <Stack className='gap-8 p-6 lg:justify-center lg:gap-4 lg:p-8 lg:pr-2'>
      <WaterHoldingCapacity />
      {isLargeScreen && <Divider className='border-dashed' />}
      <SoilMoisture />
      <WaterHolidingCapacityExplainer />
    </Stack>
  );
};

const WaterHoldingCapacity = () => {
  const { t } = useTranslation();

  const { analysisType } = useNCCardContext();

  const latestAnalysisTranslation = t('shared.ncaDetail.details.labels.latestAnalysis');

  const whcLatestAnalysis = useFact<number>(
    analysisType === 'total'
      ? 'r1_water_holding_capacity_latest_analysis_total'
      : 'r1_water_holding_capacity_latest_analysis_per_ha',
  ) as unknown as PlotReportFactElement;

  const latestAnalysisDate = printLatestDate([whcLatestAnalysis?.measured_at]);

  return (
    <div className='flex flex-col gap-8 lg:gap-4'>
      <NCCardSectionHeader
        title={t('shared.ncaDetail.details.water.labels.waterHoldingCapacity')}
        supplementaryInfo={`${latestAnalysisTranslation} ${latestAnalysisDate}`}
        popover={{
          title: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.title'),
          body: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.body'),
        }}
      />
      <Stack direction='row' className='flex-wrap gap-16'>
        <NCFactCapsule
          label={latestAnalysisTranslation}
          fact={whcLatestAnalysis}
          capsuleIconProps={{
            icon: getNCChangeIcon(whcLatestAnalysis),
            variant: getNCChangeIconVariant(whcLatestAnalysis),
          }}
          infoPopoverProps={
            whcLatestAnalysis?.measured_at
              ? {
                  title: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacityLatestAnalysis.title'),
                  body: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacityLatestAnalysis.body', {
                    date: latestAnalysisDate,
                  }),
                }
              : null
          }
        />
      </Stack>
    </div>
  );
};

const SoilMoisture = () => {
  const { t } = useTranslation();

  const { analysisType } = useNCCardContext();

  const latestAnalysisTranslation = t('shared.ncaDetail.details.labels.latestAnalysis');

  const soilMoistureLatestAnalysis = useFact<number>(
    analysisType === 'total' ? 'r1_soil_moisture_latest_analysis_total' : 'r1_soil_moisture_latest_analysis_per_ha',
  ) as unknown as PlotReportFactElement;

  const lastMeasuredDate = printLatestDate([soilMoistureLatestAnalysis?.measured_at]);

  return (
    <div className='flex flex-col gap-8 lg:gap-4'>
      <NCCardSectionHeader
        title={t('shared.ncaDetail.details.water.labels.soilMoisture')}
        supplementaryInfo={`${latestAnalysisTranslation} ${lastMeasuredDate}`}
        popover={{
          title: t('shared.ncaDetail.details.water.explainers.soilMoisture.title'),
          body: t('shared.ncaDetail.details.water.explainers.soilMoisture.body'),
        }}
      />
      <NCFactCapsule
        label={latestAnalysisTranslation}
        fact={soilMoistureLatestAnalysis}
        capsuleIconProps={{
          icon: getNCChangeIcon(soilMoistureLatestAnalysis),
          variant: getNCChangeIconVariant(soilMoistureLatestAnalysis),
        }}
        infoPopoverProps={{
          title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
          body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', {
            date: lastMeasuredDate,
          }),
        }}
      />
    </div>
  );
};

const WaterHolidingCapacityExplainer = () => {
  const { t } = useTranslation();
  const customExplainerText = useFact<string>('r1_conservation_ecological_water_historic_explainer');
  const isLargeScreen = useScreenSize() === 'large';

  if (!customExplainerText?.value) {
    return null;
  }

  return (
    <>
      {isLargeScreen && <Divider className='border-dashed' />}
      <ConservationExplainerWell
        title={t('global.whatDoesThisMean')}
        body={
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(customExplainerText.value),
            }}
          />
        }
      />
    </>
  );
};
